// spec 260509-system-monitor C-3：② 段资源轮播。
//
// 单资源行（友好名称 + 当前值 + severity 染色）+ 60s 迷你 sparkline。
//
// 轮播在 ``useDerivePrimaryBottleneck`` 里实装：CPU / 内存 / GPU / 温度 / 各磁盘
// 挂载（标签统一「存储」，多盘轮番）每 4s 切下一项；首帧落在此刻最重的资源。
// 本组件**只**消费 hook 结果。
//
// 点击整段 → 概览页对应资源分块（``#cpu`` / ``#gpu`` / ``#memory`` /
// ``#temperature`` / ``#disk``），由 dashboard 的 hash 解析消费。

import type { MouseEvent, ReactElement } from 'react';

import { Sparkline } from '../../../shared/Sparkline';
import { useDerivePrimaryBottleneck } from '../../state/derive';
import {
  ringToSeries,
  useSystemMetricsStore,
} from '../../state/system-metrics-store';
import type { BottleneckKind } from '../../state/derive';
import type { SystemMetricsSeries } from '../../state/system-metrics-store';
import { severityToColor } from '../../state/tokens';
import { navigateToDashboard } from '../navigateToDashboard';

/** GPU spark：取每秒主卡 (idx=0) util，与 KpiBar 一致。 */
function buildGpuSpark(series: SystemMetricsSeries): Array<number | null> {
  return ringToSeries(series.gpu_util).map((arr) =>
    arr === null || arr.length === 0 ? null : (arr[0] ?? null),
  );
}

function pickSpark(
  kind: BottleneckKind,
  series: SystemMetricsSeries,
): Array<number | null> {
  switch (kind) {
    case 'cpu':
      return ringToSeries(series.cpu_percent);
    case 'memory':
      return ringToSeries(series.mem_percent);
    case 'temperature':
      return ringToSeries(series.temp_main);
    case 'gpu':
      return buildGpuSpark(series);
    case 'disk':
      // 磁盘瓶颈用读速率作 sparkline 代理（最忙挂载没有 60s percent series）。
      return ringToSeries(series.disk_read_bps);
    default:
      return [];
  }
}

export function BottleneckSection(): ReactElement | null {
  const series = useSystemMetricsStore((s) => s.series);
  const bottleneck = useDerivePrimaryBottleneck();

  if (bottleneck === null) {
    // store 还没拿到 metrics → ② 段渲染占位行而不是 null（避免 widget 高度跳变）。
    return (
      <div
        className="sysmon-section sysmon-bottleneck sysmon-bottleneck-loading"
        data-section="bottleneck"
      >
        <span className="sysmon-bottleneck-label">采集中…</span>
      </div>
    );
  }

  const sparkPoints = pickSpark(bottleneck.kind, series);

  const valueDisplay =
    bottleneck.kind === 'temperature'
      ? `${Math.round(bottleneck.value)}℃`
      : `${Math.round(bottleneck.value)}%`;

  const anchor = `#${bottleneck.kind}`;
  const onClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    navigateToDashboard(bottleneck.kind);
  };

  return (
    <a
      className={`sysmon-section sysmon-bottleneck sysmon-bottleneck-${bottleneck.severity}`}
      data-section="bottleneck"
      data-kind={bottleneck.kind}
      data-severity={bottleneck.severity}
      href={anchor}
      onClick={onClick}
      aria-label={`${bottleneck.label}：${valueDisplay}`}
    >
      <span className="sysmon-bottleneck-label">{bottleneck.label}</span>
      <span className="sysmon-bottleneck-value">{valueDisplay}</span>
      <Sparkline
        className="sysmon-bottleneck-spark"
        points={sparkPoints}
        color={severityToColor(bottleneck.severity)}
      />
    </a>
  );
}

export default BottleneckSection;
